import type { Poem, Stanza } from '@maria-cavalheiro-website/shared-types'

import type { PoemFigure } from '../scenes/scene-art.ts'
import { stanzaFigures } from '../scenes/stanza-art.ts'

/**
 * One stanza, the figure that illustrates it when it carries one, and which side
 * of the spread it takes.
 *
 * A LEAF, not a "row" or a "block": the poem route reads as an illustrated book
 * now, and this is the unit a book is made of. Above 48em an illustrated leaf is a
 * flex row with the verse on one side and the picture on the other; below it the
 * leaf stacks and the picture sits above its stanza. A leaf with no picture is the
 * verse across the whole spread, at either width.
 */
export interface PoemLeaf {
  readonly stanza: Stanza
  /**
   * Present on the two or three stanzas this poem illustrates, absent on the rest.
   *
   * ABSENCE IS THE COMMON CASE NOW, not the defensive one. It was written for the
   * poem added ahead of its artwork — a stanza with no picture rather than a thrown
   * render, and no non-null assertion, which is banned in production code — and
   * that reading still holds for a chosen stanza whose cast entry is missing. Since
   * phase 0021 it is also what most stanzas get, because a poem draws a selection
   * rather than the whole cast; see `illustratedStanzas`.
   */
  readonly figure: PoemFigure | undefined
  /** Which track the verse takes. Purely visual — see `poem-article.tsx`. */
  readonly side: LeafSide
}

export type LeafSide = 'start' | 'end'

/** The ceiling: no poem draws more than this, however many stanzas it has. */
export const MAX_FIGURES_PER_POEM = 3

/** At this many stanzas or fewer, a poem takes two pictures rather than three. */
export const SHORT_POEM_STANZAS = 4

/**
 * Which stanzas of a poem of this length carry a picture, in stanza order.
 *
 * TWO OR THREE PER POEM, NOT ONE PER STANZA. Phase 0018 gave every stanza its own
 * figure, which read as a picture book at six stanzas and as clutter at seven: the
 * verse spent the whole page beside a column of small icons, each one competing
 * with the line it sat next to, and a poem was never allowed a plain reading.
 * Two or three let each picture land — it marks a place in the poem instead of
 * annotating every place at once.
 *
 * THE FIRST STANZA, THE LAST, AND ONE IN BETWEEN. Spreading to both ends rather
 * than to the interior is what puts the poem's own subject — the dove, the ladder,
 * the clown, the cat — beside the line that introduces it, and leaves a picture at
 * the foot of the poem rather than a bare stanza above the signature. The middle
 * one is the midpoint, rounded, so the spacing is even to within a stanza.
 *
 * DERIVED, NEVER AUTHORED, for the same reason the alternation below is. A
 * selection stored per poem would be twenty-three more places to get a layout
 * wrong, and it would put a layout decision inside a file whose whole subject is
 * which drawing answers which line.
 *
 * O(k) in the number of pictures, which is at most three. Total for every input:
 * a poem with one stanza illustrates it, and a poem with none illustrates nothing.
 */
export function illustratedStanzas(stanzaCount: number): readonly number[] {
  const wanted = stanzaCount <= SHORT_POEM_STANZAS ? 2 : MAX_FIGURES_PER_POEM
  const count = Math.min(wanted, stanzaCount)

  // One picture has no interval to spread across, and no stanzas have nowhere to
  // put one. Both are guarded here rather than in the formula, which divides by
  // `count - 1`.
  if (count <= 1) {
    return count === 1 ? [0] : []
  }

  return Array.from({ length: count }, (_unused, slot) =>
    Math.round((slot * (stanzaCount - 1)) / (count - 1)),
  )
}

/**
 * The poem as a sequence of leaves, two or three of which carry a picture.
 *
 * O(n) over the stanzas, one pass, with a lookup table of at most three entries.
 * Pure and total: every input is a `Poem`, whose `scene` is a validated enum, so
 * the cast lookup cannot miss.
 *
 * THE FIGURE BESIDE A STANZA IS THAT STANZA'S OWN. The cast in `stanza-art.ts` is
 * still curated line by line, and an illustrated leaf takes the entry at its own
 * index — never the next unused one. A poem drawing fewer pictures must not start
 * drawing the wrong ones.
 *
 * THE ALTERNATION IS DERIVED, AND IT COUNTS PICTURES RATHER THAN STANZAS. Parity
 * over every stanza was right when every stanza had a figure; with a selection it
 * would put all of a four-stanza poem's pictures on the same side, because the
 * chosen indices share a parity as often as not. Counting the illustrated leaves
 * gives the zigzag back exactly. Leaves with no picture have nothing to place, so
 * they take the start side and their `row-reverse` never has a second child to
 * reverse.
 */
export function poemLeaves(poem: Poem): readonly PoemLeaf[] {
  const figures = stanzaFigures(poem.scene)
  const slots = new Map(
    illustratedStanzas(poem.stanzas.length).map((stanzaIndex, slot) => [stanzaIndex, slot]),
  )

  return poem.stanzas.map((stanza, index) => {
    const slot = slots.get(index)

    return {
      stanza,
      figure: slot === undefined ? undefined : figures[index],
      side: slot !== undefined && slot % 2 === 1 ? 'end' : 'start',
    }
  })
}
